import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  AutoComplete,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Upload,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useToast } from '../../contexts/ToastContext'
import { useAuthorization } from '../../contexts/AuthorizationContext'
import { downloadBlob } from '../../lib/download'
import { useAuthSession } from '../../lib/session'
import { useStaffDataEligibility } from '../../hooks/useStaffDataEligibility'
import {
  useStaffList,
  useStaffClientUsers,
  useStaffDetails,
  useStaffData,
  useStaffMonthlyBudget,
  useUpdateStaffDetailsMutation,
  useUpdateStaffDataMutation,
  useUpdateStaffMonthlyBudgetMutation,
  useAddStaffMutation,
  useDeleteStaffMutation,
  useUploadStaffFilesMutation,
  useUploadMonthlyBudgetMutation,
} from '../../hooks/useStaff'
import {
  downloadStaffDetailsById,
  downloadStaffDataById,
  downloadStaffMonthlyBudget,
} from '../../api/staff'
import type {
  StaffGridRow,
  StaffDetailsFormValues,
  StaffDataRow,
  StaffMonthlyBudgetRow,
  MonthlyBudgetBreakdown,
} from '../../types/staff'

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000'
const NUMERIC_REGEX = /^-?[0-9]\d*(\.\d+)?$/
const CURRENT_ROWS_COUNT = 4

const BASE_BREAKDOWN_OPTIONS: { label: string; value: MonthlyBudgetBreakdown }[] = [
  { label: 'Business-wide', value: 'Business-wide' },
  { label: 'Client Partner', value: 'Client Partner' },
  { label: 'Client Manager', value: 'Client Manager' },
  { label: 'Job Partner', value: 'Job Partner' },
  { label: 'Job Manager', value: 'Job Manager' },
  { label: 'Job Type', value: 'Job Type' },
  { label: 'Team Member', value: 'Team Member' },
]

function toUtcMonthStart(value: dayjs.Dayjs | null) {
  if (!value) return null
  const month = String(value.month() + 1).padStart(2, '0')
  return `${value.year()}-${month}-01T00:00:00Z`
}

type StaffSortOrder = 'ascend' | 'descend' | null

/** Per-column comparators for the master grid. Team Manager is intentionally omitted (not sortable). */
const STAFF_SORT_COMPARATORS: Record<string, (a: StaffGridRow, b: StaffGridRow) => number> = {
  staffName: (a, b) => a.staffName.localeCompare(b.staffName),
  teamName: (a, b) => a.teamName.localeCompare(b.teamName),
  employmentStartDate: (a, b) =>
    (a.employmentStartDate || '').localeCompare(b.employmentStartDate || ''),
  employmentEndDate: (a, b) =>
    (a.employmentEndDate || '').localeCompare(b.employmentEndDate || ''),
}

/**
 * Active = still employed: no end date, or an end date that is today or in the
 * future (the 2100/2199 sentinels). An end date equal to the current date still
 * counts as active; only end dates strictly before today are inactive.
 */
function isActiveStaff(row: StaffGridRow) {
  if (!row.employmentEndDate) return true
  return !dayjs(row.employmentEndDate).isBefore(dayjs(), 'day')
}

export default function ClientStaffPage() {
  const { clientId } = useAuthorization()
  const { addToast } = useToast()
  const session = useAuthSession()
  const clientDisplayName = session?.currentClientName?.trim() || clientId
  const { isEligible: hasAccessToMonthlyBudgets } = useStaffDataEligibility(clientId)

  // --- Filters ---
  const [nameFilter, setNameFilter] = useState('')

  // --- Staff list ---
  const { data: staffListData, isLoading, isError } = useStaffList(clientId, { staffName: '' })
  const { data: clientUserOptions = [] } = useStaffClientUsers(clientId)
  const staffRows = staffListData?.rows ?? []
  const filteredStaffRows = useMemo(() => {
    const query = nameFilter.trim().toLowerCase()
    if (!query) return staffRows

    return staffRows.filter((row) => row.staffName.toLowerCase().includes(query))
  }, [nameFilter, staffRows])

  // --- Master grid sorting (active/inactive grouping is always preserved) ---
  const [staffSort, setStaffSort] = useState<{ columnKey: string; order: StaffSortOrder }>({
    columnKey: '',
    order: null,
  })

  const displayStaffRows = useMemo(() => {
    const active: StaffGridRow[] = []
    const inactive: StaffGridRow[] = []
    for (const row of filteredStaffRows) {
      ;(isActiveStaff(row) ? active : inactive).push(row)
    }

    const comparator = staffSort.order ? STAFF_SORT_COMPARATORS[staffSort.columnKey] : undefined
    if (comparator) {
      const dir = staffSort.order === 'descend' ? -1 : 1
      const sortFn = (a: StaffGridRow, b: StaffGridRow) => comparator(a, b) * dir
      active.sort(sortFn)
      inactive.sort(sortFn)
    }

    // Active group always rendered above the inactive group.
    return [...active, ...inactive]
  }, [filteredStaffRows, staffSort])

  // --- Modal state ---
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)

  // --- Detail queries ---
  const {
    data: detailData,
    isFetching: isDetailFetching,
  } = useStaffDetails(selectedUuid || '', clientId, !!selectedUuid && modalOpen)
  const { data: rawStaffData } = useStaffData(selectedUuid || '', clientId, !!selectedUuid && modalOpen)
  const { data: rawBudgetData } = useStaffMonthlyBudget(
    selectedUuid || '',
    clientId,
    !!selectedUuid && modalOpen && hasAccessToMonthlyBudgets
  )

  // --- Form ---
  const [form] = Form.useForm<StaffDetailsFormValues>()

  // --- Team manager suggestions ---
  // Match legacy behavior by combining client users from detail/initialise
  // with staff names already present in the staff grid.
  const userOptions = useMemo(() => {
    const merged = new Map<string, string>()

    for (const user of clientUserOptions) {
      if (user.name && !merged.has(user.name)) merged.set(user.name, user.name)
    }

    for (const row of staffRows) {
      if (row.staffName && !merged.has(row.staffName)) {
        merged.set(row.staffName, row.staffName)
      }
    }

    return Array.from(merged, ([value, name]) => ({ label: name, value }))
  }, [clientUserOptions, staffRows])

  const staffNameOptions = useMemo(
    () =>
      Array.from(new Set(clientUserOptions.map((option) => option.name).filter(Boolean))).map((name) => ({
        value: name,
      })),
    [clientUserOptions]
  )

  // --- Editable grid state ---
  const [staffDataRows, setStaffDataRows] = useState<StaffDataRow[]>([])
  const [historicalStaffDataRows, setHistoricalStaffDataRows] = useState<StaffDataRow[]>([])
  const [monthlyBudgetRows, setMonthlyBudgetRows] = useState<StaffMonthlyBudgetRow[]>([])
  const [historicalBudgetRows, setHistoricalBudgetRows] = useState<StaffMonthlyBudgetRow[]>([])

  const removedStaffDataRef = useRef<StaffDataRow[]>([])
  const removedBudgetRef = useRef<StaffMonthlyBudgetRow[]>([])

  const [showHistoricalStaffData, setShowHistoricalStaffData] = useState(false)
  const [showHistoricalBudget, setShowHistoricalBudget] = useState(false)

  // --- Change tracking ---
  const initialFormRef = useRef<StaffDetailsFormValues | null>(null)
  const [hasGridChanges1, setHasGridChanges1] = useState(false)
  const [hasGridChanges2, setHasGridChanges2] = useState(false)
  const [formVersion, setFormVersion] = useState(0)

  // --- Validation error flags ---
  const [showDateError, setShowDateError] = useState(false)
  const [showError1, setShowError1] = useState(false)
  const [showError2, setShowError2] = useState(false)
  const [showDuplicateError, setShowDuplicateError] = useState(false)
  const [showValueIncompleteError1, setShowValueIncompleteError1] = useState(false)
  const [showValueIncompleteError2, setShowValueIncompleteError2] = useState(false)

  // --- Mutations ---
  const updateDetailsMutation = useUpdateStaffDetailsMutation()
  const updateDataMutation = useUpdateStaffDataMutation()
  const updateBudgetMutation = useUpdateStaffMonthlyBudgetMutation()
  const addStaffMutation = useAddStaffMutation()
  const deleteMutation = useDeleteStaffMutation()
  const uploadStaffMutation = useUploadStaffFilesMutation()
  const uploadBudgetMutation = useUploadMonthlyBudgetMutation()

  // --- Add Staff modal ---
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [addForm] = Form.useForm<StaffDetailsFormValues>()
  const [addShowDateError, setAddShowDateError] = useState(false)
  const [downloadingStaff, setDownloadingStaff] = useState(false)
  const [downloadingBudget, setDownloadingBudget] = useState(false)
  const [staffUploadOpen, setStaffUploadOpen] = useState(false)
  const [budgetUploadOpen, setBudgetUploadOpen] = useState(false)
  const [staffUploadErrors, setStaffUploadErrors] = useState<string[]>([])
  const [budgetUploadError, setBudgetUploadError] = useState<string | null>(null)
  const [staffDetailsFile, setStaffDetailsFile] = useState<File | null>(null)
  const [staffDataFile, setStaffDataFile] = useState<File | null>(null)
  const [budgetFile, setBudgetFile] = useState<File | null>(null)
  const [addFormVersion, setAddFormVersion] = useState(0)

  const teamManagerOptions = useMemo(() => {
    const currentStaffName = form.getFieldValue('staffName')
    if (!currentStaffName) return userOptions
    return userOptions.filter((o) => o.value !== currentStaffName)
  }, [userOptions, formVersion])

  const addTeamManagerOptions = useMemo(() => {
    const currentStaffName = addForm.getFieldValue('staffName')
    if (!currentStaffName) return userOptions
    return userOptions.filter((o) => o.value !== currentStaffName)
  }, [userOptions, addFormVersion])

  const openAddModal = () => {
    addForm.setFieldsValue({
      staffName: '',
      email: '',
      teamName: '',
      teamManager: [],
      additionalAccess: '',
      employmentStartDate: null,
      employmentEndDate: null,
    })
    setAddShowDateError(false)
    setAddFormVersion((v) => v + 1)
    setAddModalOpen(true)
  }

  const handleAddSave = async () => {
    try {
      const values = await addForm.validateFields()

      const startDate = values.employmentStartDate
        ? dayjs(values.employmentStartDate)
        : null
      const endDate = values.employmentEndDate
        ? dayjs(values.employmentEndDate)
        : null

      if (startDate && endDate && !startDate.isBefore(endDate)) {
        setAddShowDateError(true)
        return
      }
      setAddShowDateError(false)

      await addStaffMutation.mutateAsync({
        staffDetails: {
          staffName: values.staffName,
          email: values.email,
          teamName: values.teamName,
          teamManager: (values.teamManager || []).filter(Boolean).join('|'),
          additionalAccess: values.additionalAccess,
          employmentStartDate: startDate ? startDate.format('YYYY-MM-DD') : null,
          employmentEndDate: endDate ? endDate.format('YYYY-MM-DD') : '2199-01-01T00:00:00Z',
        },
        clientId,
      })
      addToast('Staff record created', 'success')
      setAddModalOpen(false)
    } catch (err) {
      if (typeof err === 'object' && err !== null && 'errorFields' in err) return
      addToast(err instanceof Error ? err.message : 'Failed to create staff record', 'error')
    }
  }

  // --- Download helpers ---
  const handleDownloadStaffFiles = async () => {
    setDownloadingStaff(true)
    try {
      const [detailsBlob, dataBlob] = await Promise.all([
        downloadStaffDetailsById('', clientId),
        downloadStaffDataById('', clientId),
      ])
      downloadBlob(detailsBlob, 'staff details.csv')
      downloadBlob(dataBlob, 'staff data.csv')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to download staff files', 'error')
    } finally {
      setDownloadingStaff(false)
    }
  }

  const handleDownloadMonthlyBudgets = async () => {
    setDownloadingBudget(true)
    try {
      const blob = await downloadStaffMonthlyBudget('', clientId)
      downloadBlob(blob, 'staff monthly budget.csv')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to download monthly budgets', 'error')
    } finally {
      setDownloadingBudget(false)
    }
  }

  // --- Upload handlers ---
  const handleUploadStaffFiles = async () => {
    if (!staffDetailsFile && !staffDataFile) return
    try {
      const result = await uploadStaffMutation.mutateAsync({
        detailsFile: staffDetailsFile || undefined,
        dataFile: staffDataFile || undefined,
        clientId,
      })
      if (result.errors.length > 0) {
        setStaffUploadErrors(result.errors)
        return
      }
      if (result.warnings.length > 0) {
        result.warnings.forEach((w) => addToast(w, 'success'))
      } else {
        addToast('Staff files uploaded successfully', 'success')
      }
      setStaffUploadOpen(false)
      setStaffDetailsFile(null)
      setStaffDataFile(null)
      setStaffUploadErrors([])
    } catch {
      setStaffUploadErrors(['Failed to upload staff files'])
    }
  }

  const handleUploadBudget = async () => {
    if (!budgetFile) return
    try {
      const result = await uploadBudgetMutation.mutateAsync({ file: budgetFile, clientId })
      if (result.error) {
        setBudgetUploadError(result.error)
        return
      }
      addToast('Monthly budget uploaded successfully', 'success')
      setBudgetUploadOpen(false)
      setBudgetFile(null)
      setBudgetUploadError(null)
    } catch {
      setBudgetUploadError('There are invalid inputs in the Monthly Budget File. Please verify the data and try again.')
    }
  }

  // --- Populate data when queries resolve ---
  useEffect(() => {
    if (!detailData?.details || !modalOpen) return
    const d = detailData.details
    // Filter out the staff member's own name from team managers
    const filteredManagers = (d.teamManager || []).filter((tm) => tm !== d.staffName)
    form.setFieldsValue({
      ...d,
      teamManager: filteredManagers,
      employmentStartDate: (d.employmentStartDate ? dayjs(d.employmentStartDate) : null) as unknown as string,
      employmentEndDate: (d.employmentEndDate ? dayjs(d.employmentEndDate) : null) as unknown as string,
    })
    initialFormRef.current = { ...d, teamManager: filteredManagers }
    setFormVersion((v) => v + 1)
  }, [detailData, modalOpen, form])

  useEffect(() => {
    if (!rawStaffData || !modalOpen) return
    setStaffDataRows(rawStaffData.slice(0, CURRENT_ROWS_COUNT))
    setHistoricalStaffDataRows(rawStaffData.slice(CURRENT_ROWS_COUNT))
  }, [rawStaffData, modalOpen])

  useEffect(() => {
    if (!rawBudgetData || !modalOpen) return
    setMonthlyBudgetRows(rawBudgetData)
    setHistoricalBudgetRows(rawBudgetData.slice(CURRENT_ROWS_COUNT))
  }, [rawBudgetData, modalOpen])

  // --- Form change detection ---
  const hasFormChanges = useMemo(() => {
    if (!initialFormRef.current) return false
    void formVersion // dependency to re-evaluate
    try {
      const current = form.getFieldsValue()
      const normalized = {
        ...current,
        employmentStartDate: current.employmentStartDate
          ? dayjs(current.employmentStartDate).format('YYYY-MM-DD')
          : null,
        employmentEndDate: current.employmentEndDate
          ? dayjs(current.employmentEndDate).format('YYYY-MM-DD')
          : null,
      }
      return JSON.stringify(normalized) !== JSON.stringify(initialFormRef.current)
    } catch {
      return false
    }
  }, [form, formVersion])

  const hasChanges = hasFormChanges || hasGridChanges1 || hasGridChanges2

  const isBlankValue = useCallback((value: unknown) => {
    if (value === null || value === undefined) return true
    if (typeof value === 'string') return value.trim() === ''
    return false
  }, [])

  // --- Validation ---
  const runValidation = useCallback(() => {
    // Effective date validation for staff data rows
    let dateErr = false
    for (const row of staffDataRows) {
      if (row.effectiveDateStart && row.effectiveDateEnd) {
        if (new Date(row.effectiveDateEnd) <= new Date(row.effectiveDateStart)) {
          dateErr = true
          break
        }
      }
    }
    setShowDateError(dateErr)

    // Numeric validation for staff data
    const numericFields: (keyof StaffDataRow)[] = [
      'monHours', 'tueHours', 'wedHours', 'thuHours', 'friHours',
      'annualSalary', 'salaryHourlyRate', 'productivity', 'billing', 'recoverability',
    ]
    let numErr1 = false
    for (const row of staffDataRows) {
      for (const field of numericFields) {
        const val = row[field]
        if (val !== '' && val !== null && val !== undefined) {
          if (!NUMERIC_REGEX.test(String(val))) {
            numErr1 = true
            break
          }
        }
      }
      if (numErr1) break
    }
    setShowError1(numErr1)

    // Completeness validation for staff data
    let incomplete1 = false
    const requiredStaffDataFields: (keyof StaffDataRow)[] = [
      'effectiveDateStart',
      'effectiveDateEnd',
      'employmentStatus',
      'monHours',
      'tueHours',
      'wedHours',
      'thuHours',
      'friHours',
      'productivity',
      'annualSalary',
      'salaryHourlyRate',
      'recoverability',
      'billing',
    ]
    for (const row of staffDataRows) {
      if (requiredStaffDataFields.some((field) => isBlankValue(row[field]))) {
        incomplete1 = true
        break
      }
    }
    setShowValueIncompleteError1(incomplete1)

    // Monthly budget validations
    let numErr2 = false
    let incomplete2 = false
    let dupErr = false

    if (hasAccessToMonthlyBudgets) {
      for (const row of monthlyBudgetRows) {
        if (row.budget !== '' && row.budget !== null && row.budget !== undefined) {
          if (!NUMERIC_REGEX.test(String(row.budget))) {
            numErr2 = true
            break
          }
        }
      }

      for (const row of monthlyBudgetRows) {
        if (!row.breakdown || !row.month || row.budget === '' || row.budget === null || row.budget === undefined) {
          incomplete2 = true
          break
        }
      }

      const seen = new Set<string>()
      for (const row of monthlyBudgetRows) {
        if (row.breakdown && row.month) {
          const key = `${row.breakdown}|${row.month}`
          if (seen.has(key)) {
            dupErr = true
            break
          }
          seen.add(key)
        }
      }
    }

    setShowError2(numErr2)
    setShowValueIncompleteError2(incomplete2)
    setShowDuplicateError(dupErr)

    return !dateErr && !numErr1 && !incomplete1 && !numErr2 && !incomplete2 && !dupErr
  }, [staffDataRows, monthlyBudgetRows, hasAccessToMonthlyBudgets, isBlankValue])

  // Re-run validation when grid data changes
  useEffect(() => {
    if (modalOpen) runValidation()
  }, [staffDataRows, monthlyBudgetRows, modalOpen, runValidation])

  // --- Cell change handlers ---
  const handleCellChange = useCallback(
    (targetRow: StaffDataRow, field: keyof StaffDataRow, value: unknown) => {
      setStaffDataRows((prev) =>
        prev.map((row) => (row === targetRow ? { ...row, [field]: value } : row))
      )
      setHasGridChanges1(true)
    },
    []
  )

  const handleBudgetCellChange = useCallback(
    (targetRow: StaffMonthlyBudgetRow, field: keyof StaffMonthlyBudgetRow, value: unknown) => {
      setMonthlyBudgetRows((prev) =>
        prev.map((row) => (row === targetRow ? { ...row, [field]: value } : row))
      )
      setHasGridChanges2(true)
    },
    []
  )

  // --- Add/Delete rows ---
  const handleAddStaffDataRow = () => {
    const newRow: StaffDataRow = {
      id: EMPTY_UUID,
      uuid: selectedUuid || '',
      effectiveDateEnd: null,
      effectiveDateStart: null,
      employmentStatus: '',
      monHours: '',
      tueHours: '',
      wedHours: '',
      thuHours: '',
      friHours: '',
      productivity: '',
      annualSalary: '',
      salaryHourlyRate: '',
      billing: '',
      recoverability: '',
      isNewRecord: true,
    }
    setStaffDataRows((prev) => [newRow, ...prev])
    setHasGridChanges1(true)
  }

  const handleDeleteStaffDataRow = (targetRow: StaffDataRow) => {
    if (!targetRow.isNewRecord) {
      removedStaffDataRef.current = [...removedStaffDataRef.current, targetRow]
    }
    setStaffDataRows((prev) => prev.filter((row) => row !== targetRow))
    setHasGridChanges1(true)
  }

  const handleAddBudgetRow = () => {
    const newRow: StaffMonthlyBudgetRow = {
      staffMonthlyBudgetId: EMPTY_UUID,
      staffId: selectedUuid || '',
      clientId,
      breakdown: 'Business-wide',
      month: null,
      budget: '',
      isNewRecord: true,
    }
    setMonthlyBudgetRows((prev) => [newRow, ...prev])
    setHasGridChanges2(true)
  }

  const handleDeleteBudgetRow = (targetRow: StaffMonthlyBudgetRow) => {
    if (!targetRow.isNewRecord) {
      removedBudgetRef.current = [...removedBudgetRef.current, targetRow]
    }
    setMonthlyBudgetRows((prev) => prev.filter((row) => row !== targetRow))
    setHasGridChanges2(true)
  }

  // --- Open/Close modal ---
  const openModal = (uuid: string) => {
    setSelectedUuid(uuid)
    setModalOpen(true)
    setHasGridChanges1(false)
    setHasGridChanges2(false)
    setShowHistoricalStaffData(false)
    setShowHistoricalBudget(false)
    removedStaffDataRef.current = []
    removedBudgetRef.current = []
    initialFormRef.current = null
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedUuid(null)
    form.resetFields()
    setStaffDataRows([])
    setHistoricalStaffDataRows([])
    setMonthlyBudgetRows([])
    setHistoricalBudgetRows([])
    setHasGridChanges1(false)
    setHasGridChanges2(false)
    setShowDateError(false)
    setShowError1(false)
    setShowError2(false)
    setShowDuplicateError(false)
    setShowValueIncompleteError1(false)
    setShowValueIncompleteError2(false)
  }

  const handleClose = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Unsaved changes',
        content: 'There are unsaved changes, are you sure you want to close the screen?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: closeModal,
      })
    } else {
      closeModal()
    }
  }

  // --- Delete record ---
  const handleDeleteRecord = () => {
    if (!selectedUuid) return
    Modal.confirm({
      title: 'Delete staff record',
      content: 'Are you sure you want to delete this staff record?',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync({ uuid: selectedUuid, clientId })
          addToast('Staff record deleted', 'warning')
          closeModal()
        } catch (err) {
          addToast(err instanceof Error ? err.message : 'Failed to delete staff record', 'error')
        }
      },
    })
  }

  // --- Save ---
  const handleSave = async () => {
    const formValid = await form
      .validateFields()
      .then(() => true)
      .catch(() => false)
    if (!formValid) return

    const validationOk = runValidation()
    if (!validationOk) return
    if (!hasChanges) return

    try {
      if (hasFormChanges) {
        const values = form.getFieldsValue()
        await updateDetailsMutation.mutateAsync({
          clientId,
          uuid: selectedUuid!,
          staffName: values.staffName,
          email: values.email,
          teamName: values.teamName,
          teamManager: (values.teamManager || []).filter(Boolean).join('|'),
          additionalAccess: values.additionalAccess,
          employmentStartDate: values.employmentStartDate
            ? dayjs(values.employmentStartDate).format('YYYY-MM-DD')
            : null,
          employmentEndDate: values.employmentEndDate
            ? dayjs(values.employmentEndDate).format('YYYY-MM-DD')
            : null,
        })
      }
      if (hasGridChanges1) {
        await updateDataMutation.mutateAsync({
          staffData: staffDataRows,
          removedStaffData: removedStaffDataRef.current,
          clientId,
        })
      }
      if (hasGridChanges2 && hasAccessToMonthlyBudgets) {
        await updateBudgetMutation.mutateAsync({
          staffMonthlyBudget: monthlyBudgetRows,
          removedStaffMonthlyBudget: removedBudgetRef.current,
          clientId,
        })
      }
      addToast('Staff data saved successfully', 'success')
      closeModal()
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to save staff data', 'error')
    }
  }

  const isSaving =
    updateDetailsMutation.isPending || updateDataMutation.isPending || updateBudgetMutation.isPending

  const hasValidationErrors =
    showDateError || showError1 || showError2 || showDuplicateError ||
    showValueIncompleteError1 || showValueIncompleteError2

  // --- Master grid columns ---
  const gridColumns: ColumnsType<StaffGridRow> = useMemo(
    () => [
      {
        title: 'Staff Name',
        dataIndex: 'staffName',
        key: 'staffName',
        sorter: true,
        sortOrder: staffSort.columnKey === 'staffName' ? staffSort.order : null,
        render: (value: string, row: StaffGridRow) => (
          <button
            type="button"
            onClick={() => openModal(row.uuid)}
            className="font-medium text-gray-900 hover:text-brand-600 text-left"
          >
            {value}
          </button>
        ),
      },
      {
        title: 'Team Name',
        dataIndex: 'teamName',
        key: 'teamName',
        sorter: true,
        sortOrder: staffSort.columnKey === 'teamName' ? staffSort.order : null,
      },
      {
        title: 'Team Manager',
        dataIndex: 'teamManager',
        key: 'teamManager',
        render: (value: string) => (value ? value.replace(/\|/g, ', ') : ''),
      },
      {
        title: 'Employment Start Date',
        dataIndex: 'employmentStartDate',
        key: 'employmentStartDate',
        sorter: true,
        sortOrder: staffSort.columnKey === 'employmentStartDate' ? staffSort.order : null,
        render: (value: string | null) =>
          value ? dayjs(value).format('DD-MM-YYYY') : '',
      },
      {
        title: 'Employment End Date',
        dataIndex: 'employmentEndDate',
        key: 'employmentEndDate',
        sorter: true,
        sortOrder: staffSort.columnKey === 'employmentEndDate' ? staffSort.order : null,
        render: (value: string | null) =>
          value ? dayjs(value).format('DD-MM-YYYY') : '',
      },
    ],
    [staffSort]
  )

  // --- Staff data editable columns ---
  const staffDataColumns: ColumnsType<StaffDataRow> = useMemo(
    () => [
      {
        title: '',
        key: 'delete',
        width: 50,
        render: (_: unknown, row: StaffDataRow) => (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteStaffDataRow(row)}
          />
        ),
      },
      {
        title: 'Effective Date Start',
        dataIndex: 'effectiveDateStart',
        key: 'effectiveDateStart',
        width: 155,
        render: (value: string | null, row: StaffDataRow) => (
          <DatePicker
            value={value ? dayjs(value) : null}
            format="DD-MM-YYYY"
            size="small"
            style={{ width: '100%' }}
            onChange={(date) =>
              handleCellChange(row, 'effectiveDateStart', date ? date.toISOString() : null)
            }
          />
        ),
      },
      {
        title: 'Effective Date End',
        dataIndex: 'effectiveDateEnd',
        key: 'effectiveDateEnd',
        width: 150,
        render: (value: string | null, row: StaffDataRow) => (
          <DatePicker
            value={value ? dayjs(value) : null}
            format="DD-MM-YYYY"
            size="small"
            style={{ width: '100%' }}
            onChange={(date) =>
              handleCellChange(row, 'effectiveDateEnd', date ? date.toISOString() : null)
            }
          />
        ),
      },
      {
        title: 'Employment Status',
        dataIndex: 'employmentStatus',
        key: 'employmentStatus',
        width: 140,
        render: (value: string, row: StaffDataRow) => (
          <Input
            value={value}
            size="small"
            onChange={(e) => handleCellChange(row, 'employmentStatus', e.target.value)}
          />
        ),
      },
      ...(['monHours', 'tueHours', 'wedHours', 'thuHours', 'friHours'] as const).map(
        (field) => ({
          title: `${field.replace('Hours', '').replace(/^./, (c) => c.toUpperCase())} Hours`,
          dataIndex: field,
          key: field,
          width: 105,
          render: (value: number | string, row: StaffDataRow) => (
            <InputNumber
              value={value === '' ? null : value}
              size="small"
              style={{ width: '100%' }}
              onChange={(val) => handleCellChange(row, field, val ?? '')}
            />
          ),
        })
      ),
      {
        title: '% Productivity',
        dataIndex: 'productivity',
        key: 'productivity',
        width: 130,
        render: (value: number | string, row: StaffDataRow) => (
          <InputNumber
            value={value === '' ? null : typeof value === 'number' ? value * 100 : value}
            size="small"
            style={{ width: '100%' }}
            formatter={(val) => (val != null && val !== '' ? `${val}%` : '')}
            parser={(val) => val?.replace('%', '') as unknown as number}
            onChange={(val) =>
              handleCellChange(row, 'productivity', val != null ? Number(val) / 100 : '')
            }
          />
        ),
      },
      {
        title: 'Annual Salary',
        dataIndex: 'annualSalary',
        key: 'annualSalary',
        width: 130,
        render: (value: number | string, row: StaffDataRow) => (
          <InputNumber
            value={value === '' ? null : value}
            size="small"
            style={{ width: '100%' }}
            onChange={(val) => handleCellChange(row, 'annualSalary', val ?? '')}
          />
        ),
      },
      {
        title: 'Salary Hourly Rate',
        dataIndex: 'salaryHourlyRate',
        key: 'salaryHourlyRate',
        width: 150,
        render: (value: number | string, row: StaffDataRow) => (
          <InputNumber
            value={value === '' ? null : value}
            size="small"
            style={{ width: '100%' }}
            onChange={(val) => handleCellChange(row, 'salaryHourlyRate', val ?? '')}
          />
        ),
      },
      {
        title: 'Recoverability',
        dataIndex: 'recoverability',
        key: 'recoverability',
        width: 150,
        render: (value: number | string, row: StaffDataRow) => (
          <InputNumber
            value={value === '' ? null : typeof value === 'number' ? value * 100 : value}
            size="small"
            style={{ width: '100%' }}
            formatter={(val) => (val != null && val !== '' ? `${val}%` : '')}
            parser={(val) => val?.replace('%', '') as unknown as number}
            onChange={(val) =>
              handleCellChange(row, 'recoverability', val != null ? Number(val) / 100 : '')
            }
          />
        ),
      },
      {
        title: 'Billing',
        dataIndex: 'billing',
        key: 'billing',
        width: 150,
        render: (value: number | string, row: StaffDataRow) => (
          <InputNumber
            value={value === '' ? null : value}
            size="small"
            style={{ width: '100%' }}
            onChange={(val) => handleCellChange(row, 'billing', val ?? '')}
          />
        ),
      },
    ],
    [handleCellChange]
  )

  // --- Historical staff data columns (read-only, no delete) ---
  const historicalStaffDataColumns: ColumnsType<StaffDataRow> = useMemo(
    () => [
      {
        title: 'Effective Date Start',
        dataIndex: 'effectiveDateStart',
        key: 'effectiveDateStart',
        width: 155,
        render: (value: string | null) => (value ? dayjs(value).format('DD-MM-YYYY') : ''),
      },
      {
        title: 'Effective Date End',
        dataIndex: 'effectiveDateEnd',
        key: 'effectiveDateEnd',
        width: 150,
        render: (value: string | null) => (value ? dayjs(value).format('DD-MM-YYYY') : ''),
      },
      { title: 'Employment Status', dataIndex: 'employmentStatus', key: 'employmentStatus', width: 140 },
      { title: 'Mon Hours', dataIndex: 'monHours', key: 'monHours', width: 105 },
      { title: 'Tue Hours', dataIndex: 'tueHours', key: 'tueHours', width: 98 },
      { title: 'Wed Hours', dataIndex: 'wedHours', key: 'wedHours', width: 105 },
      { title: 'Thu Hours', dataIndex: 'thuHours', key: 'thuHours', width: 100 },
      { title: 'Fri Hours', dataIndex: 'friHours', key: 'friHours', width: 98 },
      {
        title: '% Productivity',
        dataIndex: 'productivity',
        key: 'productivity',
        width: 130,
        render: (value: number | string) =>
          value !== '' && value != null ? `${(Number(value) * 100).toFixed(2)}%` : '',
      },
      { title: 'Annual Salary', dataIndex: 'annualSalary', key: 'annualSalary', width: 130 },
      { title: 'Salary Hourly Rate', dataIndex: 'salaryHourlyRate', key: 'salaryHourlyRate', width: 150 },
      {
        title: 'Recoverability',
        dataIndex: 'recoverability',
        key: 'recoverability',
        width: 150,
        render: (value: number | string) =>
          value !== '' && value != null ? `${(Number(value) * 100).toFixed(2)}%` : '',
      },
      { title: 'Billing', dataIndex: 'billing', key: 'billing', width: 150 },
    ],
    []
  )

  // --- Monthly budget editable columns ---
  const budgetColumns: ColumnsType<StaffMonthlyBudgetRow> = useMemo(
    () => [
      {
        title: '',
        key: 'delete',
        width: 50,
        render: (_: unknown, row: StaffMonthlyBudgetRow) => (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteBudgetRow(row)}
          />
        ),
      },
      {
        title: 'Breakdown',
        dataIndex: 'breakdown',
        key: 'breakdown',
        width: 200,
        render: (value: string, row: StaffMonthlyBudgetRow) => (
          <Select
            value={value}
            size="small"
            style={{ width: '100%' }}
            options={BASE_BREAKDOWN_OPTIONS}
            onChange={(val) => handleBudgetCellChange(row, 'breakdown', val)}
          />
        ),
      },
      {
        title: 'Months',
        dataIndex: 'month',
        key: 'month',
        width: 150,
        render: (value: string | null, row: StaffMonthlyBudgetRow) => (
          <DatePicker
            picker="month"
            value={value ? dayjs(value) : null}
            format="MMM-YYYY"
            size="small"
            style={{ width: '100%' }}
            onChange={(date) =>
              handleBudgetCellChange(
                row,
                'month',
                toUtcMonthStart(date)
              )
            }
          />
        ),
      },
      {
        title: 'Budget',
        dataIndex: 'budget',
        key: 'budget',
        width: 200,
        render: (value: number | string, row: StaffMonthlyBudgetRow) => (
          <InputNumber
            value={value === '' ? null : value}
            size="small"
            style={{ width: '100%' }}
            onChange={(val) => handleBudgetCellChange(row, 'budget', val ?? '')}
          />
        ),
      },
    ],
    [handleBudgetCellChange]
  )

  // --- Historical budget columns (read-only) ---
  const historicalBudgetColumns: ColumnsType<StaffMonthlyBudgetRow> = useMemo(
    () => [
      {
        title: 'Breakdown',
        dataIndex: 'breakdown',
        key: 'breakdown',
        width: 200,
      },
      {
        title: 'Months',
        dataIndex: 'month',
        key: 'month',
        width: 150,
        render: (value: string | null) => (value ? dayjs(value).format('MMM-YYYY') : ''),
      },
      {
        title: 'Budget',
        dataIndex: 'budget',
        key: 'budget',
        width: 200,
      },
    ],
    []
  )

  // --- Render ---
  return (
    <div className="rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white to-rose-50/30">
      <div className="px-6 pt-5 pb-4 border-b border-brand-100 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Staff Data</h1>
          <p className="text-sm text-gray-500 mt-1">Client: {clientDisplayName}</p>
        </div>
      </div>

      <div className="p-4 bg-white/70 flex flex-wrap items-center gap-2">
        <Input
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          placeholder="Search by staff name"
          suffix={<SearchOutlined />}
          allowClear
          style={{ width: 280 }}
        />
        <Button icon={<PlusOutlined />} onClick={openAddModal}>
          Add
        </Button>
        <Button loading={downloadingStaff} onClick={() => void handleDownloadStaffFiles()}>
          Download Staff Files
        </Button>
        {hasAccessToMonthlyBudgets && (
          <Button loading={downloadingBudget} onClick={() => void handleDownloadMonthlyBudgets()}>
            Download Monthly Budgets
          </Button>
        )}
        <Button icon={<UploadOutlined />} onClick={() => { setStaffUploadErrors([]); setStaffUploadOpen(true) }}>
          Upload Staff Files
        </Button>
        {hasAccessToMonthlyBudgets && (
          <Button icon={<UploadOutlined />} onClick={() => { setBudgetUploadError(null); setBudgetUploadOpen(true) }}>
            Upload Monthly Budgets
          </Button>
        )}
      </div>

      <div className="p-4">
        {isError ? (
          <Alert type="error" message="Failed to load staff data" showIcon />
        ) : (
          <Table<StaffGridRow>
            rowKey="uuid"
            loading={isLoading}
            columns={gridColumns}
            dataSource={displayStaffRows}
            onChange={(_pagination, _filters, sorter) => {
              const s = Array.isArray(sorter) ? sorter[0] : sorter
              setStaffSort({
                columnKey: (s?.columnKey as string) ?? '',
                order: (s?.order as StaffSortOrder) ?? null,
              })
            }}
            pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        open={modalOpen}
        width="80vw"
        title="Staff Details"
        onCancel={handleClose}
        destroyOnHidden
        footer={(_, { CancelBtn }) => (
          <Space>
            <Button danger onClick={handleDeleteRecord} loading={deleteMutation.isPending}>
              Delete Record
            </Button>
            <Button
              type="primary"
              onClick={() => void handleSave()}
              loading={isSaving}
              disabled={!hasChanges || hasValidationErrors}
            >
              Save Changes
            </Button>
            <CancelBtn />
          </Space>
        )}
      >
        {isDetailFetching ? (
          <div className="flex justify-center py-8">
            <span className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* --- Staff Details Form --- */}
            <Form
              form={form}
              layout="vertical"
              onValuesChange={() => setFormVersion((v) => v + 1)}
            >
              <div className="grid grid-cols-3 gap-4">
                <Form.Item
                  name="staffName"
                  label="Staff Name"
                  rules={[{ required: true, message: 'Staff name is required' }]}
                >
                  <AutoComplete
                    options={staffNameOptions}
                    placeholder="Enter staff name"
                    filterOption={(inputValue, option) =>
                      String(option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase())
                    }
                    onChange={() => setFormVersion((v) => v + 1)}
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Email format is invalid' },
                  ]}
                >
                  <Input placeholder="Enter email" />
                </Form.Item>
                <Form.Item name="teamName" label="Team Name">
                  <Input placeholder="Team name" />
                </Form.Item>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-2">
                  <Form.Item name="teamManager" label="Team Manager">
                    <Select
                      mode="multiple"
                      options={teamManagerOptions}
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      placeholder="Select team managers"
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  name="employmentStartDate"
                  label="Employment Start Date"
                  rules={[{ required: true, message: 'Start date is required' }]}
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: '100%' }}
                    onChange={() => setFormVersion((v) => v + 1)}
                  />
                </Form.Item>
                <Form.Item name="employmentEndDate" label="Employment End Date">
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: '100%' }}
                    onChange={() => setFormVersion((v) => v + 1)}
                  />
                </Form.Item>
              </div>
            </Form>
            {/* --- Staff Data Section --- */}
            <div>
              <h3 className="font-semibold text-base mb-2">Staff Data</h3>
              <p className="text-xs text-gray-500 mb-2">
                Press the Enter key upon changing the value on each row to save it.
              </p>

              {showDateError && (
                <Alert
                  type="error"
                  message="Effective date end must be greater than effective date start."
                  className="mb-2"
                  showIcon
                />
              )}
              {showError1 && (
                <Alert
                  type="error"
                  message="Numeric fields contain invalid values."
                  className="mb-2"
                  showIcon
                />
              )}
              {showValueIncompleteError1 && (
                <Alert
                  type="error"
                  message="All editable staff data rows must have every field completed."
                  className="mb-2"
                  showIcon
                />
              )}

              <Space className="mb-2">
                {!showHistoricalStaffData && (
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={handleAddStaffDataRow}
                  >
                    Add New Record
                  </Button>
                )}
                <Button
                  size="small"
                  type="link"
                  onClick={() => setShowHistoricalStaffData((v) => !v)}
                >
                  {showHistoricalStaffData ? 'Hide' : 'Show'} Historical Data
                </Button>
              </Space>

              {!showHistoricalStaffData && (
                <Table<StaffDataRow>
                  rowKey={(_, index) => `sd-${index}`}
                  columns={staffDataColumns}
                  dataSource={staffDataRows}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  size="small"
                />
              )}

              {showHistoricalStaffData && (
                <div className="mt-4">
                  <h4 className="font-medium text-sm mb-1 text-gray-600">Historical Staff Data</h4>
                  <Table<StaffDataRow>
                    rowKey={(_, index) => `hsd-${index}`}
                    columns={historicalStaffDataColumns}
                    dataSource={historicalStaffDataRows}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                    size="small"
                  />
                </div>
              )}
            </div>

            {/* --- Monthly Budget Section (conditional) --- */}
            {hasAccessToMonthlyBudgets && (
              <div>
                <h3 className="font-semibold text-base mb-2">Monthly Budget</h3>
                <p className="text-xs text-gray-500 mb-2">
                  Press the Enter key upon changing the value on each row to save it.
                </p>

                {showError2 && (
                  <Alert
                    type="error"
                    message="Budget field contains invalid numeric values."
                    className="mb-2"
                    showIcon
                  />
                )}
                {showDuplicateError && (
                  <Alert
                    type="error"
                    message="Duplicate breakdown and month combination found."
                    className="mb-2"
                    showIcon
                  />
                )}
                {showValueIncompleteError2 && (
                  <Alert
                    type="error"
                    message="All budget rows must have breakdown, month, and budget filled."
                    className="mb-2"
                    showIcon
                  />
                )}

                <Space className="mb-2">
                  {!showHistoricalBudget && (
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={handleAddBudgetRow}
                    >
                      Add New Monthly Budget
                    </Button>
                  )}
                  <Button
                    size="small"
                    type="link"
                    onClick={() => setShowHistoricalBudget((v) => !v)}
                  >
                    {showHistoricalBudget ? 'Hide' : 'Show'} Historical Data
                  </Button>
                </Space>

                {!showHistoricalBudget && (
                  <Table<StaffMonthlyBudgetRow>
                    rowKey={(_, index) => `mb-${index}`}
                    columns={budgetColumns}
                    dataSource={monthlyBudgetRows}
                    pagination={{ pageSize: 5, showSizeChanger: false }}
                    scroll={{ x: 'max-content' }}
                    size="small"
                  />
                )}

                {showHistoricalBudget && (
                  <div className="mt-4">
                    <h4 className="font-medium text-sm mb-1 text-gray-600">
                      Historical Monthly Budget
                    </h4>
                    <Table<StaffMonthlyBudgetRow>
                      rowKey={(_, index) => `hmb-${index}`}
                      columns={historicalBudgetColumns}
                      dataSource={historicalBudgetRows}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 'max-content' }}
                      size="small"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Add Staff Modal */}
      <Modal
        open={addModalOpen}
        width="50vw"
        title="Add New Staff"
        onCancel={() => setAddModalOpen(false)}
        footer={
          <Space>
            <Button
              type="primary"
              onClick={() => void handleAddSave()}
              loading={addStaffMutation.isPending}
            >
              Create
            </Button>
            <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
          </Space>
        }
      >
        {addShowDateError && (
          <Alert
            type="error"
            message="Employment end date should be greater than employment start date."
            className="mb-4"
            showIcon
          />
        )}
        <Form form={addForm} layout="vertical" onValuesChange={() => setAddFormVersion((v) => v + 1)}>
          <div className="grid grid-cols-3 gap-4">
            <Form.Item
              name="staffName"
              label="Staff Name"
              rules={[{ required: true, message: 'Staff name is required' }]}
            >
              <AutoComplete
                options={staffNameOptions}
                placeholder="Enter staff name"
                filterOption={(inputValue, option) =>
                  String(option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase())
                }
                onChange={() => setAddFormVersion((v) => v + 1)}
              />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Email format is invalid' },
              ]}
            >
              <Input placeholder="Enter email" />
            </Form.Item>
            <Form.Item name="teamName" label="Team Name">
              <Input placeholder="Team name" />
            </Form.Item>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <Form.Item name="teamManager" label="Team Manager">
                <Select
                  mode="multiple"
                  options={addTeamManagerOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Select team managers"
                />
              </Form.Item>
            </div>
            <Form.Item
              name="employmentStartDate"
              label="Employment Start Date"
              rules={[{ required: true, message: 'Start date is required' }]}
            >
              <DatePicker
                format="DD-MM-YYYY"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item name="employmentEndDate" label="Employment End Date">
              <DatePicker
                format="DD-MM-YYYY"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Upload Staff Files Modal */}
      <Modal
        open={staffUploadOpen}
        title="Upload Staff Files"
        onCancel={() => {
          setStaffUploadOpen(false)
          setStaffDetailsFile(null)
          setStaffDataFile(null)
          setStaffUploadErrors([])
        }}
        footer={
          <Space>
            <Button
              type="primary"
              onClick={() => void handleUploadStaffFiles()}
              loading={uploadStaffMutation.isPending}
              disabled={!staffDetailsFile && !staffDataFile}
            >
              Upload
            </Button>
            <Button onClick={() => {
              setStaffUploadOpen(false)
              setStaffDetailsFile(null)
              setStaffDataFile(null)
              setStaffUploadErrors([])
            }}>
              Cancel
            </Button>
          </Space>
        }
      >
        {staffUploadErrors.length > 0 && (
          <Alert
            type="error"
            message="Upload failed"
            description={
              <ul className="list-disc pl-4 mb-0">
                {staffUploadErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            }
            showIcon
            className="mb-4"
          />
        )}
        <Alert
          type="warning"
          message="Existing records may be overwritten."
          className="mb-4"
          showIcon
        />
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-1">Staff Details CSV</p>
            <Upload
              accept=".csv"
              maxCount={1}
              beforeUpload={(file) => {
                setStaffDetailsFile(file)
                return false
              }}
              onRemove={() => setStaffDetailsFile(null)}
              fileList={staffDetailsFile ? [{ uid: '-1', name: staffDetailsFile.name, status: 'done' }] : []}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Staff Data CSV</p>
            <Upload
              accept=".csv"
              maxCount={1}
              beforeUpload={(file) => {
                setStaffDataFile(file)
                return false
              }}
              onRemove={() => setStaffDataFile(null)}
              fileList={staffDataFile ? [{ uid: '-1', name: staffDataFile.name, status: 'done' }] : []}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </div>
        </div>
      </Modal>

      {/* Upload Monthly Budget Modal */}
      <Modal
        open={budgetUploadOpen}
        title="Upload Monthly Budgets"
        onCancel={() => {
          setBudgetUploadOpen(false)
          setBudgetFile(null)
          setBudgetUploadError(null)
        }}
        footer={
          <Space>
            <Button
              type="primary"
              onClick={() => void handleUploadBudget()}
              loading={uploadBudgetMutation.isPending}
              disabled={!budgetFile}
            >
              Upload
            </Button>
            <Button onClick={() => {
              setBudgetUploadOpen(false)
              setBudgetFile(null)
              setBudgetUploadError(null)
            }}>
              Cancel
            </Button>
          </Space>
        }
      >
        {budgetUploadError && (
          <Alert
            type="error"
            message={budgetUploadError}
            showIcon
            className="mb-4"
          />
        )}
        <Alert
          type="warning"
          message="Existing records may be overwritten."
          className="mb-4"
          showIcon
        />
        <div>
          <p className="text-sm font-medium mb-1">Monthly Budget CSV</p>
          <Upload
            accept=".csv"
            maxCount={1}
            beforeUpload={(file) => {
              setBudgetFile(file)
              return false
            }}
            onRemove={() => setBudgetFile(null)}
            fileList={budgetFile ? [{ uid: '-1', name: budgetFile.name, status: 'done' }] : []}
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
        </div>
      </Modal>
    </div>
  )
}
